/** Gap after which a new centered timestamp separator is shown. */
export const TIME_GROUP_GAP_MS = 30 * 60 * 1000;

/**
 * Parse a message timestamp into epoch ms.
 * @param {Object|null|undefined} message
 * @returns {number|null}
 */
export function getMessageTimeMs(message) {
  const raw = message?.created_at || message?.timestamp;
  if (raw == null || raw === "") return null;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Whether a centered timestamp separator should appear before `current`.
 * Messages within TIME_GROUP_GAP_MS are grouped under one label (any senders).
 * System messages never get a separator; they break the previous group.
 * @param {Object|null|undefined} prev
 * @param {Object|null|undefined} current
 * @param {number} [gapMs=TIME_GROUP_GAP_MS]
 * @returns {boolean}
 */
export function shouldShowTimeSeparator(
  prev,
  current,
  gapMs = TIME_GROUP_GAP_MS
) {
  if (!current || current.message_type === "system") return false;
  if (!prev || prev.message_type === "system") return true;

  const prevTime = getMessageTimeMs(prev);
  const currTime = getMessageTimeMs(current);
  if (prevTime == null || currTime == null) return true;

  return currTime - prevTime >= gapMs;
}

/**
 * Format a centered time-group label.
 * Today: time only. Within the past week: weekday + time. Older: full date + time.
 * @param {string|number|Date} [timestamp]
 * @returns {string}
 */
export function formatTimeSeparatorLabel(timestamp) {
  if (timestamp == null || timestamp === "") return "";
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";

  const time = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return time;
  }

  const weekMs = 7 * 24 * 60 * 60 * 1000;
  if (now.getTime() - date.getTime() >= weekMs) {
    const fullDate = date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${fullDate}, ${time}`;
  }

  const weekday = date.toLocaleDateString([], { weekday: "short" });
  return `${weekday} ${time}`;
}
